import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  Typography,
  Box,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  CircularProgress,
  Chip,
  Tooltip,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import RefreshIcon from '@mui/icons-material/Refresh';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PaymentsIcon from '@mui/icons-material/Payments';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import announcementSignalRService from '../../services/announcementSignalRService';
import { getRenewalNotices, type RenewalNoticeItem } from '../../services/companyBillingService';
import RenewalPaymentFlow from '../billing/RenewalPaymentFlow';
import {
  useDueReminders,
  DUE_REMINDER_SOURCE_TEXTS,
  formatDueReminderAmount,
  type DueReminderGroup,
  type DueReminderItem,
} from '../../contexts/DueRemindersContext';
import { buildReminderHref, reminderSearchFromItem } from '../../utils/reminderDeepLink';

/**
 * NotificationMenu — Header'daki bildirim zili.
 *
 * Her vade kalemini ayrı satırda listeler (rozet sayısı ile aynı adet).
 * Liste kaydırılabilir; başlık sabit kalır.
 */

const StyledBadge = styled(Badge)(() => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#d32f2f',
    color: 'white',
  },
}));

const MenuHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: '#f5f5f5',
  borderBottom: '1px solid #e0e0e0',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
}));

const EmptyState = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const SOURCE_ICONS: Record<string, React.ReactNode> = {
  LOAN_INSTALLMENT: <RequestQuoteIcon />,
  CREDIT_CARD: <CreditCardIcon />,
  CHECK_NOTE_GIVEN: <ReceiptLongIcon />,
  CHECK_NOTE_RECEIVED: <ReceiptLongIcon />,
};

interface ReminderRow {
  key: string;
  group: DueReminderGroup;
  item: DueReminderItem;
}

const fallbackItemsFromGroup = (group: DueReminderGroup): DueReminderItem[] => {
  if (group.items && group.items.length > 0) return group.items;

  const rows: DueReminderItem[] = [];
  if (group.overdueCount > 0) {
    rows.push({
      label: group.mostOverdueLabel || '',
      dueDate: '',
      amount: group.overdueTotals?.[0]?.amount ?? 0,
      currency: group.overdueTotals?.[0]?.currency ?? 'TRY',
      daysToDue: group.mostOverdueDays ? -group.mostOverdueDays : -1,
    });
  }
  if (group.upcomingCount > 0) {
    rows.push({
      label: group.nearestLabel || '',
      dueDate: group.nearestDueDate || '',
      amount: group.upcomingTotals?.[0]?.amount ?? 0,
      currency: group.upcomingTotals?.[0]?.currency ?? 'TRY',
      daysToDue: 0,
    });
  }
  return rows;
};

const NotificationMenu: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notices, setNotices] = useState<RenewalNoticeItem[]>([]);
  const [payToken, setPayToken] = useState<string | null>(null);
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { userDetails, refreshCompany } = useAuth();
  const { groups, totalCount, loading, enabled, refresh } = useDueReminders();

  const companyId = userDetails?.companyId;
  const isAdminRole = userDetails?.role === 'admin' || userDetails?.role === 'subadmin';

  const loadNotices = useCallback(async () => {
    if (!companyId || !isAdminRole) {
      setNotices([]);
      return;
    }
    try {
      setNotices(await getRenewalNotices());
    } catch (err) {
      console.warn('[NotificationMenu] Tahsilat bildirimleri alınamadı:', err);
    }
  }, [companyId, isAdminRole]);

  useEffect(() => {
    if (!companyId || !isAdminRole) return;
    loadNotices();
    const cbId = 'customer_panel_renewal_notices';
    announcementSignalRService.connect(companyId).catch(() => {});
    announcementSignalRService.onRenewalNotice(cbId, () => {
      loadNotices();
    });
    const interval = window.setInterval(loadNotices, 60 * 60 * 1000);
    return () => {
      window.clearInterval(interval);
      announcementSignalRService.removeRenewalNoticeCallback(cbId);
    };
  }, [companyId, isAdminRole, loadNotices]);

  const visibleNotices = notices;

  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);
  const localeCode = isTr ? 'tr-TR' : 'en-US';

  const rows = useMemo<ReminderRow[]>(() => {
    const flattened = groups
      .flatMap((group) =>
        fallbackItemsFromGroup(group).map((item, index) => ({
          key: `${group.source}-${item.label}-${item.dueDate}-${index}`,
          group,
          item,
        }))
      )
      .sort((a, b) => a.item.daysToDue - b.item.daysToDue);

    console.log('[NotificationMenu] liste', {
      groupCount: groups.length,
      rowCount: flattened.length,
      badgeTotalCount: totalCount,
      usedFallback: groups.some((g) => !g.items || g.items.length === 0),
      groups: groups.map((g) => ({
        source: g.source,
        overdueCount: g.overdueCount,
        upcomingCount: g.upcomingCount,
        itemsLength: g.items?.length ?? 0,
      })),
    });

    return flattened;
  }, [groups, totalCount]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    refresh();
    loadNotices();
  };

  const handleClose = () => setAnchorEl(null);

  const handleGoTo = (group: DueReminderGroup, item: DueReminderItem) => {
    setAnchorEl(null);
    navigate(buildReminderHref(group.route, reminderSearchFromItem(item)));
  };

  const badgeCount = totalCount + visibleNotices.length;
  if (!enabled && visibleNotices.length === 0) return null;

  const sourceLabel = (sourceType: string) => {
    if (sourceType === 'FiscalDevice') return tt('Yazarkasa', 'Fiscal device');
    if (sourceType === 'Contract') return tt('Lisans', 'License');
    return tt('Tahsilat', 'Collection');
  };

  const openPayment = (token: string) => {
    setAnchorEl(null);
    setPayToken(token);
  };

  return (
    <>
      <Tooltip title={tt('Bildirimler', 'Notifications')}>
        <IconButton color="inherit" onClick={handleOpen} sx={{ marginRight: 2 }}>
          <StyledBadge badgeContent={badgeCount > 0 ? badgeCount : null} max={99}>
            <NotificationsIcon />
          </StyledBadge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{ sx: { width: 440, maxHeight: 640, overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}
      >
        <MenuHeader>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {tt('Bildirimler', 'Notifications')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {badgeCount > 0
                ? tt(`${badgeCount} kalem takip gerektiriyor`, `${badgeCount} item(s) need attention`)
                : tt('Bekleyen bildirim yok', 'No pending notifications')}
            </Typography>
          </Box>
          <Tooltip title={tt('Yenile', 'Refresh')}>
            <IconButton size="small" onClick={() => { refresh(); loadNotices(); }} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </MenuHeader>

        <Box sx={{ maxHeight: 520, overflowY: 'auto' }}>
          {loading && rows.length === 0 && visibleNotices.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : rows.length > 0 || visibleNotices.length > 0 ? (
            <List disablePadding>
              {visibleNotices.map((notice) => {
                const overdue = notice.daysRemaining < 0;
                const amount = formatDueReminderAmount(notice.totalAmount, notice.currency, localeCode);
                const timing = overdue
                  ? tt(`${Math.abs(notice.daysRemaining)} gün gecikti`, `${Math.abs(notice.daysRemaining)} day(s) late`)
                  : notice.daysRemaining === 0
                    ? tt('Vadesi bugün', 'Due today')
                    : tt(`${notice.daysRemaining} gün kaldı`, `${notice.daysRemaining} day(s) left`);
                const dueDateText = notice.expiryDate
                  ? new Date(notice.expiryDate).toLocaleDateString(localeCode)
                  : '';
                return (
                  <React.Fragment key={`renewal-${notice.id}`}>
                    <ListItemButton
                      onClick={() => openPayment(notice.token)}
                      sx={{
                        alignItems: 'flex-start',
                        borderLeft: '4px solid',
                        borderLeftColor: overdue ? 'error.main' : 'warning.main',
                        py: 1.25,
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: overdue ? 'error.main' : 'warning.main', color: 'white' }}>
                          <PaymentsIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div' }}
                        primary={
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
                              {notice.title}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                              <Chip label={sourceLabel(notice.sourceType)} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                              <Chip
                                label={tt('Tahsilat', 'Collection')}
                                size="small"
                                color={overdue ? 'error' : 'warning'}
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            </Box>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.75 }}>
                            <Typography variant="body2" color="text.primary" fontWeight="bold">
                              {amount}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              {dueDateText ? `${timing} · ${dueDateText}` : timing}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                    <Divider component="li" />
                  </React.Fragment>
                );
              })}
              {rows.map((row) => {
                const texts = DUE_REMINDER_SOURCE_TEXTS[row.group.source];
                if (!texts) return null;

                const typeName = tt(texts.nameTr, texts.nameEn);
                const overdue = row.item.daysToDue < 0;
                const isReceivable = row.group.direction === 'RECEIVABLE';
                const amount = formatDueReminderAmount(row.item.amount, row.item.currency, localeCode);

                const timing = overdue
                  ? tt(`${Math.abs(row.item.daysToDue)} gün gecikti`, `${Math.abs(row.item.daysToDue)} day(s) late`)
                  : row.item.daysToDue === 0
                    ? tt('Vadesi bugün', 'Due today')
                    : tt(`${row.item.daysToDue} gün kaldı`, `${row.item.daysToDue} day(s) left`);

                const dueDateText = row.item.dueDate
                  ? new Date(row.item.dueDate).toLocaleDateString(localeCode)
                  : '';

                return (
                  <React.Fragment key={row.key}>
                    <ListItemButton
                      onClick={() => handleGoTo(row.group, row.item)}
                      sx={{
                        alignItems: 'flex-start',
                        borderLeft: '4px solid',
                        borderLeftColor: overdue ? 'error.main' : 'warning.main',
                        py: 1.25,
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: overdue ? 'error.main' : 'warning.main', color: 'white' }}>
                          {SOURCE_ICONS[row.group.source] ?? <EventBusyIcon />}
                        </Avatar>
                      </ListItemAvatar>

                      <ListItemText
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div' }}
                        primary={
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
                              {row.item.label || typeName}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                              <Chip
                                label={typeName}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                              <Chip
                                label={overdue ? tt('Gecikmiş', 'Overdue') : tt('Yaklaşıyor', 'Due soon')}
                                size="small"
                                color={overdue ? 'error' : 'warning'}
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                              <Chip
                                label={isReceivable ? tt('Tahsilat', 'Collection') : tt('Ödeme', 'Payment')}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            </Box>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.75 }}>
                            <Typography variant="body2" color="text.primary" fontWeight="bold">
                              {amount}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                              {dueDateText ? `${timing} · ${dueDateText}` : timing}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                    <Divider component="li" />
                  </React.Fragment>
                );
              })}
            </List>
          ) : (
            <EmptyState>
              <Typography>{tt('Bekleyen vade bildirimi yok', 'No pending due reminders')}</Typography>
              <Typography variant="caption">
                {tt(
                  'Tahsilat, kredi, kredi kartı ve çek/senet burada listelenir.',
                  'Collections, loans, credit cards and checks/notes appear here.'
                )}
              </Typography>
            </EmptyState>
          )}
        </Box>
      </Menu>

      <Dialog open={!!payToken} onClose={() => setPayToken(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          {tt('Tahsilat', 'Collection')}
          <IconButton aria-label="Kapat" onClick={() => setPayToken(null)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {payToken && (
            <RenewalPaymentFlow
              token={payToken}
              onPaid={() => {
                void refreshCompany();
                loadNotices();
              }}
              onClose={() => {
                setPayToken(null);
                loadNotices();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationMenu;
